import { FITBIT_REQUIRED_SCOPE } from "../constants/fitbitScope";

/**
 * Unified Fitbit Configuration for Frontend
 * Centralized configuration for all Fitbit OAuth operations
 */

export interface FitbitConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  authorizationUrl: string;
  tokenUrl: string;
  scope: string;
}

class FitbitConfigManager {
  private config: FitbitConfig;

  constructor() {
    this.config = this.buildConfig();
    this.logConfig();
  }

  private buildConfig(): FitbitConfig {
    const clientId = process.env.REACT_APP_FITBIT_CLIENT_ID || '';
    // Client secret should NEVER be on frontend - it's only used on backend
    const clientSecret = ''; // Not used on frontend
    const redirectUri = this.getRedirectUri();
    
    return {
      clientId,
      clientSecret,
      redirectUri,
      authorizationUrl: 'https://www.fitbit.com/oauth2/authorize',
      tokenUrl: 'https://api.fitbit.com/oauth2/token',
      scope: FITBIT_REQUIRED_SCOPE
    };
  }

  private getRedirectUri(): string {
    // Use dev_mode environment variable to determine environment
    const devMode = process.env.REACT_APP_DEV_MODE || 'development';
    const explicitRedirectUri = process.env.REACT_APP_FITBIT_REDIRECT_URI;
    const clientUrl = process.env.REACT_APP_CLIENT_URL;
    
    
    // Priority order:
    // 1. Explicit REACT_APP_FITBIT_REDIRECT_URI env var
    // 2. Based on dev_mode: localhost for development, clientUrl for production
    // 3. Fallback to localhost
    
    if (explicitRedirectUri) {
      return explicitRedirectUri;
    }
    
    if (devMode === 'development') {
      const localhostUri = 'http://localhost:3000/fitbit/callback';
      return localhostUri;
    }
    
    if (devMode === 'production' && clientUrl) {
      const redirectUri = `${clientUrl}/fitbit/callback`;
      return redirectUri;
    }
    
    // Fallback to localhost
    const fallback = 'http://localhost:3000/fitbit/callback';
    return fallback;
  }

  private logConfig(): void {
    // Configuration logging removed for production
  }

  /**
   * Get the configuration object
   */
  getConfig(): FitbitConfig {
    return this.config;
  }

  /**
   * Get authorization URL with parameters
   */
  getAuthorizationUrl(state: string): string {
    const params = new URLSearchParams({
      client_id: this.config.clientId,
      response_type: 'code',
      scope: this.config.scope,
      redirect_uri: this.config.redirectUri,
      state: state
    });

    const url = `${this.config.authorizationUrl}?${params.toString()}`;
    
    
    return url;
  }

  /**
   * Get token exchange parameters
   */
  getTokenExchangeParams(code: string, codeVerifier: string | null = null) {
    const params: Record<string, string> = {
      client_id: this.config.clientId,
      grant_type: 'authorization_code',
      redirect_uri: this.config.redirectUri,
      code: code
    };

    if (codeVerifier) {
      params.code_verifier = codeVerifier;
    }

    
    return params;
  }

  /**
   * Validate configuration
   */
  validate(): boolean {
    const errors: string[] = [];
    
    if (!this.config.clientId) {
      errors.push('REACT_APP_FITBIT_CLIENT_ID is required');
    }
    
    // Client secret is NOT required on frontend - it's only used on backend
    // if (!this.config.clientSecret) {
    //   errors.push('REACT_APP_FITBIT_CLIENT_SECRET is required');
    // }
    
    if (!this.config.redirectUri) {
      errors.push('Redirect URI could not be determined');
    }
    
    if (errors.length > 0) {
      return false;
    }
    return true;
  }

  /**
   * Get configuration summary for logging
   */
  getSummary() {
    return {
      clientId: this.config.clientId ? this.config.clientId.substring(0, 8) + '...' : 'MISSING',
      clientSecret: 'NOT USED ON FRONTEND',
      redirectUri: this.config.redirectUri,
      authorizationUrl: this.config.authorizationUrl,
      tokenUrl: this.config.tokenUrl,
      scope: this.config.scope
    };
  }
}

// Export singleton instance
export const fitbitConfig = new FitbitConfigManager();
export default fitbitConfig;
